import { useQuery, type UseQueryOptions } from 'react-query'

import { AlmanacFilesClient, buildFilesQuery } from 'api/clients/AlmanacFilesClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'
import { CalendarFilters } from 'screens/Calendar/types'

import { filesKeys } from './filesKeys'
import { type AlmanacFileShowItem } from './types'
import { useCurrentWorkspaceIdWithProject } from 'hooks/useCurrentWorkspaceIdWithProject'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

type FileQueryMeta = {
  filtersSignature?: string
  partialCalendar?: boolean
  startDate?: string | null
}

type BaseDetailKey = ReturnType<typeof filesKeys.detail>
type UseFileQueryKey = BaseDetailKey | [...BaseDetailKey, FileQueryMeta]

type UseFileOptions = UseQueryOptions<AlmanacFileShowItem, unknown, AlmanacFileShowItem, UseFileQueryKey>

interface UseFileParams extends UseFileOptions {
  filters?: CalendarFilters
  partialCalendar?: boolean
  startDate?: string | null
}

const NO_FILE_KEY = '__no-file-key__'

export const useFile = (fileKey?: string | null, options?: UseFileParams) => {
  const workspaceId = useCurrentWorkspaceIdWithProject()
  const { data: brandPlan } = useCurrentBrandPlan()
  const useStatusFilterScope = !!brandPlan?.has_content_strategies

  const { enabled: enabledOption = true, onError, filters, partialCalendar, startDate, ...restOptions } = options ?? {}

  const filtersSignature = filters ? JSON.stringify(filters) : undefined
  const normalizedFileKey = fileKey ?? NO_FILE_KEY
  const baseKey = filesKeys.detail(workspaceId, normalizedFileKey)
  const queryKey: UseFileQueryKey =
    filtersSignature || startDate || partialCalendar
      ? [...baseKey, { filtersSignature, partialCalendar, startDate: startDate ?? null }]
      : baseKey

  return useQuery<AlmanacFileShowItem, unknown, AlmanacFileShowItem, UseFileQueryKey>(
    queryKey,
    async () => {
      if (!workspaceId || !fileKey) {
        throw new Error('A workspace must be selected before fetching files.')
      }

      const response = await AlmanacFilesClient.show(workspaceId, fileKey, {
        ...(partialCalendar ? { partial_calendar: true } : {}),
        q: buildFilesQuery({ ...filters, useStatusFilterScope }),
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to load file.')
      }

      return response.data.almanac_file
    },
    {
      enabled: Boolean(workspaceId) && Boolean(fileKey) && enabledOption,
      onError: (error) => {
        reportError(error, { severity: 'error' })
        onError?.(error)
      },
      ...restOptions,
    },
  )
}
